// components/forms/VCardForm.vue
<template>
  <div class="space-y-4 min-w-0">
    <h3 class="text-lg font-semibold text-gray-800">Contact Information</h3>
    <p class="text-sm text-gray-500">At least one of Full Name or First Name is required.</p>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="min-w-0">
        <label for="vcard-firstname" class="block text-sm font-medium text-gray-700 mb-1">
          First Name
        </label>
        <input
          id="vcard-firstname"
          type="text"
          :value="modelValue.firstName"
          @input="updateField('firstName', $event.target.value)"
          :aria-invalid="!!(backendErrors && backendErrors.firstName)"
          class="w-full min-w-0 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          :class="fieldBorderClass('firstName')"
          placeholder="John"
          aria-describedby="vcard-firstname-err"
        />
        <p v-if="fieldError('firstName')" id="vcard-firstname-err" class="mt-1 text-sm text-red-600" role="alert">{{ fieldError('firstName') }}</p>
      </div>

      <div class="min-w-0">
        <label for="vcard-lastname" class="block text-sm font-medium text-gray-700 mb-1">
          Last Name
        </label>
        <input
          id="vcard-lastname"
          type="text"
          :value="modelValue.lastName"
          @input="updateField('lastName', $event.target.value)"
          :aria-invalid="!!(backendErrors && backendErrors.lastName)"
          class="w-full min-w-0 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          :class="fieldBorderClass('lastName')"
          placeholder="Doe"
          aria-describedby="vcard-lastname-err"
        />
        <p v-if="fieldError('lastName')" id="vcard-lastname-err" class="mt-1 text-sm text-red-600" role="alert">{{ fieldError('lastName') }}</p>
      </div>
    </div>

    <div class="min-w-0">
      <label for="vcard-name" class="block text-sm font-medium text-gray-700 mb-1">
        Full Name (if different)
      </label>
      <input
        id="vcard-name"
        type="text"
        :value="modelValue.name"
        @input="updateField('name', $event.target.value)"
        :aria-invalid="!!(backendErrors && backendErrors.name)"
        class="w-full min-w-0 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        :class="fieldBorderClass('name')"
        placeholder="Dr. John Doe"
        aria-describedby="vcard-name-err"
      />
      <p v-if="fieldError('name')" id="vcard-name-err" class="mt-1 text-sm text-red-600" role="alert">{{ fieldError('name') }}</p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="min-w-0">
        <label for="vcard-phone" class="block text-sm font-medium text-gray-700 mb-1">
          Phone Number
        </label>
        <input
          id="vcard-phone"
          type="tel"
          :value="modelValue.phone"
          @input="updateField('phone', $event.target.value)"
          :aria-invalid="!!(backendErrors && backendErrors.phone)"
          class="w-full min-w-0 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          :class="fieldBorderClass('phone')"
          placeholder="+1234567890"
          aria-describedby="vcard-phone-err"
        />
        <p v-if="fieldError('phone')" id="vcard-phone-err" class="mt-1 text-sm text-red-600" role="alert">{{ fieldError('phone') }}</p>
      </div>

      <div class="min-w-0">
        <label for="vcard-email" class="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <input
          id="vcard-email"
          type="email"
          :value="modelValue.email"
          @input="updateField('email', $event.target.value)"
          :aria-invalid="!!emailError"
          class="w-full min-w-0 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          :class="emailError ? 'border-red-500' : (backendErrors && backendErrors.email ? 'border-red-500' : 'border-gray-300')"
          placeholder="john@example.com"
          aria-describedby="vcard-email-err"
        />
        <p v-if="emailError || fieldError('email')" id="vcard-email-err" class="mt-1 text-sm text-red-600" role="alert">{{ emailError || fieldError('email') }}</p>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="min-w-0">
        <label for="vcard-organization" class="block text-sm font-medium text-gray-700 mb-1">
          Organization
        </label>
        <input
          id="vcard-organization"
          type="text"
          :value="modelValue.organization"
          @input="updateField('organization', $event.target.value)"
          :aria-invalid="!!(backendErrors && backendErrors.organization)"
          class="w-full min-w-0 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          :class="fieldBorderClass('organization')"
          placeholder="Company Name"
          aria-describedby="vcard-organization-err"
        />
        <p v-if="fieldError('organization')" id="vcard-organization-err" class="mt-1 text-sm text-red-600" role="alert">{{ fieldError('organization') }}</p>
      </div>

      <div class="min-w-0">
        <label for="vcard-title" class="block text-sm font-medium text-gray-700 mb-1">
          Job Title
        </label>
        <input
          id="vcard-title"
          type="text"
          :value="modelValue.title"
          @input="updateField('title', $event.target.value)"
          class="w-full min-w-0 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Software Developer"
        />
      </div>
    </div>

    <div class="min-w-0">
      <label for="vcard-website" class="block text-sm font-medium text-gray-700 mb-1">
        Website
      </label>
      <input
        id="vcard-website"
        type="url"
        :value="modelValue.website"
        @input="updateField('website', $event.target.value)"
        :aria-invalid="!!websiteError"
        class="w-full min-w-0 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        :class="websiteError ? 'border-red-500' : (backendErrors && backendErrors.website ? 'border-red-500' : 'border-gray-300')"
        placeholder="https://www.example.com"
        aria-describedby="vcard-website-err"
      />
      <p v-if="websiteError || fieldError('website')" id="vcard-website-err" class="mt-1 text-sm text-red-600" role="alert">{{ websiteError || fieldError('website') }}</p>
    </div>

    <div class="min-w-0">
      <label for="vcard-address" class="block text-sm font-medium text-gray-700 mb-1">
        Address
      </label>
      <textarea
        id="vcard-address"
        :value="modelValue.address"
        @input="updateField('address', $event.target.value)"
        class="w-full min-w-0 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        rows="2"
        placeholder="123 Main St, City, State, ZIP"
      ></textarea>
    </div>
  </div>
</template>

<script>
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(v) {
  if (!v || typeof v !== 'string') return true;
  return EMAIL_REGEX.test(v.trim());
}
function isValidUrl(v) {
  if (!v || typeof v !== 'string') return true;
  const s = v.trim();
  if (!s) return true;
  try {
    new URL(s.startsWith('http') ? s : 'https://' + s);
    return true;
  } catch {
    return false;
  }
}

export default {
  name: "VCardForm",
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    backendErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue"],
  computed: {
    emailError() {
      const v = this.modelValue?.email;
      if (!v || !v.trim()) return '';
      return isValidEmail(v) ? '' : 'Invalid email format';
    },
    websiteError() {
      const v = this.modelValue?.website;
      if (!v || !v.trim()) return '';
      return isValidUrl(v) ? '' : 'Please enter a valid URL (e.g. https://example.com)';
    },
  },
  methods: {
    updateField(field, value) {
      this.$emit("update:modelValue", {
        ...this.modelValue,
        [field]: value,
      });
    },
    fieldError(field) {
      return this.backendErrors && this.backendErrors[field] ? this.backendErrors[field] : '';
    },
    fieldBorderClass(field) {
      if (this.backendErrors && this.backendErrors[field]) return 'border-red-500';
      return 'border-gray-300';
    },
  },
};
</script>